import type { EngineResult, MergeInput } from "../merge/types";
import { ENGINE_TIMEOUT_MS } from "./provenance";
import { isWorkerReply } from "./protocol";
import type { MergeWorkerRequest } from "./protocol";

interface PendingRequest {
  readonly resolve: (result: EngineResult) => void;
  readonly reject: (error: Error) => void;
  readonly timeout: ReturnType<typeof setTimeout>;
}

export class MergeEngineClient {
  readonly #pending = new Map<number, PendingRequest>();
  #nextId = 1;
  #worker: Worker;

  constructor() {
    this.#worker = this.#createWorker();
  }

  run(input: MergeInput): Promise<EngineResult> {
    const id = this.#nextId;
    this.#nextId += 1;

    return new Promise<EngineResult>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.#pending.delete(id);
        this.#restartWorker();
        reject(
          new Error(
            `The reference engine exceeded the ${ENGINE_TIMEOUT_MS.toLocaleString()} ms UI deadline. The worker was terminated.`,
          ),
        );
      }, ENGINE_TIMEOUT_MS);

      this.#pending.set(id, { resolve, reject, timeout });

      const request: MergeWorkerRequest = {
        type: "merge",
        id,
        input,
      };
      this.#worker.postMessage(request);
    });
  }

  dispose(): void {
    this.#worker.terminate();
    this.#rejectPending("The reference engine was closed.");
  }

  #createWorker(): Worker {
    const worker = new Worker(new URL("./merge.worker.ts", import.meta.url), {
      type: "module",
      name: "libdualnative-merge-v1",
    });

    worker.addEventListener("message", (event: MessageEvent<unknown>) => {
      if (!isWorkerReply(event.data)) {
        this.#restartWorker();
        return;
      }

      const pending = this.#pending.get(event.data.id);
      if (pending === undefined) {
        return;
      }

      clearTimeout(pending.timeout);
      this.#pending.delete(event.data.id);

      if (event.data.type === "failure") {
        pending.reject(new Error(event.data.message));
        return;
      }

      pending.resolve({ response: event.data.response, raw: event.data.raw });
    });

    worker.addEventListener("error", () => {
      this.#restartWorker();
    });

    worker.addEventListener("messageerror", () => {
      this.#restartWorker();
    });

    return worker;
  }

  #restartWorker(): void {
    this.#worker.terminate();
    this.#rejectPending("The reference engine worker stopped unexpectedly.");
    this.#worker = this.#createWorker();
  }

  #rejectPending(message: string): void {
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timeout);
      pending.reject(new Error(message));
    }
    this.#pending.clear();
  }
}

